<script context="module">
	let meChecked = false;
</script>

<script>
	// Frame of every agent screen: top bar (brand, search or title, active cart) and the bottom tab bar.
	import { onMount } from 'svelte';
	import { link, location, push } from 'svelte-spa-router';
	import fetchData from '../api.js';
	import UserStore from '../stores/user';
	import { cartsStore } from '../stores/cartStore';
	import { activeCart, activeCartId, loadActiveCart } from '../stores/activeCart';
	import { money, cartTotals } from '../lib/format';
	import Icon from './Icon.svelte';

	export let title = '';

	$: totals = cartTotals($activeCart?.items || []);
	$: openCarts = Object.keys($cartsStore || {}).length;

	// the returns price lookup only for users the admin allowed; return certificates for everyone else
	// (and the admin, who sees all of them)
	$: tabs = [
		{ href: '/agents/products', label: 'מוצרים', icon: 'grid' },
		{ href: '/agents/carts', label: 'סלים פתוחים', icon: 'bag', badge: true },
		...(!$UserStore.can_returns || $UserStore.role === 'admin' ? [{ href: '/agents/return-notes', label: 'תעודות החזרה', icon: 'note' }] : []),
		{ href: '/agents/orders', label: 'הזמנות', icon: 'receipt' },
		...($UserStore.can_returns ? [{ href: '/agents/returns', label: 'החזרות', icon: 'returns' }] : []),
		{ href: '/account', label: 'החשבון שלי', icon: 'user' }
	];

	$: isOn = (href) => $location === href || $location.startsWith(href + '/') || ($location === '/' && href === '/agents/products');

	function openCart() {
		push($activeCartId ? '/agents/carts/' + $activeCartId : '/agents/carts');
	}

	async function refreshOpenCarts() {
		const res = await fetchData({ action: 'agents_get_carts', token: $UserStore.data });
		cartsStore.set((res && res.data) || {});
	}

	// role and permissions as saved on the server, once per app start: changes by the admin show without signing in again
	async function refreshMe() {
		if (meChecked || !$UserStore.data) return;
		meChecked = true;
		const res = await fetchData({ action: 'agents_me', token: $UserStore.data });
		if (!res || !res.user) return;
		const user = {
			...$UserStore,
			agent_name: res.user.name,
			role: res.user.role,
			must_change_password: Number(res.user.must_change_password) === 1,
			can_returns: Number(res.user.can_returns) === 1
		};
		localStorage.setItem('user', JSON.stringify(user));
		UserStore.set(user);
	}

	onMount(() => {
		refreshMe();
		// a temporary password (set by the admin) must be replaced before working
		if ($UserStore.must_change_password && $location !== '/account') {
			push('/account');
			return;
		}
		if (!$activeCart && $activeCartId) loadActiveCart();
	});

	// the open-carts badge changes when a cart is created, sent or deleted
	$: $activeCartId, refreshOpenCarts();
</script>

<header class="topbar">
	<div class="brand">
		<div class="logo-mark">A</div>
		<div class="brand-text">
			<b>{$UserStore.company || 'סוכנים'}</b>
			<small>{$UserStore.agent_name || ''}</small>
		</div>
	</div>

	<div class="topbar-center">
		<slot name="top">
			<h1 class="page-title">{title}</h1>
		</slot>
	</div>

	{#if $activeCart}
		<button class="cart-pill" on:click={openCart}>
			<Icon name="bag" size={22} />
			<span class="who">{$activeCart.customer_name || 'סל ללא לקוח'} <small>· {money(totals.total)}</small></span>
			<span class="count">{totals.units}</span>
		</button>
	{:else}
		<button class="cart-pill empty" on:click={openCart}>
			<Icon name="bag" size={22} />
			<span class="who">אין סל פעיל</span>
			<span class="count">0</span>
		</button>
	{/if}
</header>

<main class="page">
	<slot />
</main>

<nav class="tabbar" style="grid-template-columns: repeat({tabs.length}, 1fr)">
	{#each tabs as tab}
		<a href={tab.href} use:link class:on={isOn(tab.href)}>
			<Icon name={tab.icon} size={26} />
			<span>{tab.label}{#if tab.badge && openCarts}<span class="badge">{openCarts}</span>{/if}</span>
		</a>
	{/each}
</nav>
